import Vue from 'vue'
import Snackbar from '@/components/Snackbar'
import 'moment-timezone' // load timezone functionality
import moment from 'moment'

export default {
  namespaced: true,
  state: {
    exceptionHandlingList: [],
    exceptionHandlingDuplicates: [],
    multipleUPData: null,
    circularHierarchyData: null,
    multipleIpData: null,
    dupEntitiesData: null,
    exceptionTypeFilter: [],
    limit: 10,
    offset: 0,
    totalRecords: 0,
    searchQuery: '',
    filterOptions: null,
    upErrorRefData: null,
    failedToFetchFlag: false
  },
  actions: {
    async getExceptionHandling({ commit, state }) {
      try {
        // Construct the request payload
        const requestPayload = {
          searchQuery: state.searchQuery,
          limit: state.limit,
          exceptionType: state.exceptionTypeFilter || [],
          offset: state.offset || 0
        }
        // Make a POST request and pass the payload in the request body
        const response = await Vue.prototype.$http.post(
          'dmp/exception-handling', // API endpoint
          requestPayload // Send data as the body of the request
        )

        // Handle the response
        if (response.data.status === 200 && response.data.data) {
          commit('SET_EXCEPTION_HANDLING', response.data.data)
        } else if (response.data.status === 204) {
          // Handle no content
          commit('SET_EXCEPTION_HANDLING', [])
          // TODO: Decide what action to take for status 204
        }

        // Common Pagination Set Logic
        const paginationData = {
          limit: response.data.limit || 10,
          offset: response.data.offset || 0,
          totalRows: parseInt(response.data.totalRows) || 0
        }
        commit('SET_EXCEPTIONAL_PAGINATION_DATA', paginationData)
      } catch (error) {
        // Handle any errors
        throw new Error(error)
      }
    },
    setLimitAndOffset({ commit }, { limit = 10, offset = 0 } = {}) {
      commit('SET_LIMIT_AND_OFFSET', { limit, offset })
    },
    resetPaginationData({ commit }) {
      commit('SET_LIMIT_AND_OFFSET', { limit: 10, offset: 0 })
    },
    updateSearchQuery({ commit }, searchQuery) {
      commit('SET_SEARCH_QUERY', searchQuery)
    },
    async getExceptionHandlingDuplicates({ commit }, sgaId) {
      try {
        commit('SET_EXCEPTION_HANDLING_DUPLICATES', [])
        const response = await Vue.prototype.$http.get(
          `/dmp/exception-handling/duplicates?sgaId=${sgaId}`
        )
        if (response.data.status === 200 && response.data.data) {
          commit('SET_EXCEPTION_HANDLING_DUPLICATES', response.data.data)
        } else {
          // If API data is null or the status is not 200, set an empty array
          commit('SET_EXCEPTION_HANDLING_DUPLICATES', [])
        }
        commit('SET_FETCH_FAILED_FLAG', false)
      } catch (error) {
        Snackbar({
          message: 'Something went wrong. Please try again',
          type: 'is-danger',
          indefinite: true
        })
        commit('SET_FETCH_FAILED_FLAG', true)
        // Handle any errors here
      }
    },
    async updateMasterEntity({ commit }, updateMasterPayload) {
      try {
        const response = await Vue.prototype.$http.put(
          '/dmp/exception-handling/master-selection',
          updateMasterPayload
        )
        return response.data.status === 200 && response.data.data > 0
      } catch (error) {
        throw new Error(error)
      }
    },
    async updateDuplicateEntity({ commit }, updateDuplicatePayload) {
      try {
        const response = await Vue.prototype.$http.put(
          '/dmp/exception-handling/duplicate-selection',
          updateDuplicatePayload
        )
        return response.data.status === 200 && response.data.data > 0
      } catch (error) {
        throw new Error(error)
      }
    },
    async processAllDuplicates({ commit }, duplicateBaseSGAID) {
      try {
        const response = await Vue.prototype.$http.put(
          `/dmp/exception-handling/duplicate-processed?duplicateBaseSGAID=${duplicateBaseSGAID}`
        )
        return response.data.status === 200 && response.data.data > 0
      } catch (error) {
        throw new Error(error)
      }
    },
    async resetProcessedEntity({ commit }, resetProcessedEntityPayload) {
      try {
        const response = await Vue.prototype.$http.put(
          '/dmp/exception-handling/clear-processed',
          resetProcessedEntityPayload
        )
        return response.data.status === 200 && response.data.data > 0
      } catch (error) {
        throw new Error(error)
      }
    },
    async updateInvalidEntity({ commit }, { updateInvalidPayload, sgaId }) {
      try {
        const response = await Vue.prototype.$http.put(
          `/dmp/exception-handling/update-invalid-entity?sgaId=${sgaId}`,
          updateInvalidPayload
        )
        return response.data.status === 200 && response.data.data > 0
      } catch (error) {
        throw new Error(error)
      }
    },
    async refreshMVForException({ commit, dispatch }) {
      commit('SET_LOADER', true)
      try {
        const response = await Vue.prototype.$http.get(
          '/dmp/common/refresh-mv-exception'
        )
        return response
      } catch (error) {
        throw error
      } finally {
        commit('SET_LOADER', false)
      }
    },
    async downloadExceptionalHandlingData({ commit, state }) {
      try {
        // Get the user's timezone
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
        const requestPayload = {
          searchQuery: state.searchQuery || '',
          exceptionType: state.exceptionTypeFilter || [],
          userTimeZone
        }
        const response = await Vue.prototype.$http.post(
          '/dmp/exception-handling/download-csv',
          requestPayload,
          {
            responseType: 'blob' // Ensure the response is treated as a blob
          }
        )
        const blob = new Blob([response.data], { type: 'text/csv' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `exceptional-handling-data_${moment().format(
          'DD-MM-YYYY_HH:mm'
        )}.csv`
        link.click()
        Snackbar({
          message: 'File downloaded successfully',
          type: 'is-success'
        })
      } catch (error) {
        console.error('Error downloading CSV:', error)
        Snackbar({ message: 'Failed to download CSV.', type: 'is-danger' })
      }
    },
    updateExceptionTypeFilter({ commit }, exceptionTypeFilter) {
      commit('SET_EXCEPTIONTYPE_FILTER', exceptionTypeFilter)
    },
    updateExceptionalHandlingData({ commit }, exceptionHandlingData) {
      commit('SET_EXCEPTION_HANDLING', exceptionHandlingData)
    },
    async removeDuplicateEntity({ commit }, { notDupEntityIds, sgaId }) {
      try {
        const response = await Vue.prototype.$http.put(
          `/dmp/exception-handling/remove-duplicate-entity`,
          {
            duplicateBaseSGAID: sgaId,
            notDupEntityIds
          }
        )

        if (response.data && response.data.status === 200) {
          Snackbar({
            message: 'Entity removed successfully.',
            type: 'is-success'
          })
          return true
        } else {
          Snackbar({ message: 'Entity remove failed.', type: 'is-warning' })
          return false
        }
      } catch (error) {
        Snackbar({ message: 'Something went wrong', type: 'is-danger' })
        throw new Error(error)
      }
    },
    updatePaginationData({ commit }, paginationData) {
      commit('SET_EXCEPTIONAL_PAGINATION_DATA', paginationData)
    },

    async multipleParentValidation({ commit }, { masterSgaId, slaveSgaIds }) {
      commit('SET_MULTIPLE_UP_DATA', null)
      try {
        const response = await Vue.prototype.$http.post(
          '/dmp/exception-handling/multiple-parent-validation',
          { masterSgaId: masterSgaId, slaveSgaIds: slaveSgaIds }
        )
        if (
          response.data.status === 200 &&
          response.data?.isMUP &&
          response.data.data
        ) {
          commit('SET_MULTIPLE_UP_DATA', response.data.data)
        }
        return response.data.status === 200 && response.data.isSuccess
      } catch (error) {
        throw new Error(error)
      }
    },

    async multipleIpValidation({ commit }, { masterSgaId, slaveSgaIds }) {
      try {
        const response = await Vue.prototype.$http.post(
          '/dmp/exception-handling/multiple-immediate-parent-validation',
          { masterSgaId: masterSgaId, slaveSgaIds: slaveSgaIds }
        )
        if (response.data.status === 200 && response.data.data) {
          commit('SET_MULTIPLE_IP_DATA', response.data.data)
        }
        return response.data.status === 200 && response.data.isSuccess
      } catch (error) {
        throw new Error(error)
      }
    },

    async circularHierarchyValidation(
      { commit },
      { masterSgaId, slaveSgaIds }
    ) {
      commit('SET_CIRCULAR_HIERARCHY_DATA', null)
      try {
        const response = await Vue.prototype.$http.post(
          '/dmp/exception-handling/circular-hierarchy-validation',
          { masterSgaId: masterSgaId, slaveSgaIds: slaveSgaIds }
        )
        if (
          response.data.status === 200 &&
          response.data?.isCircular &&
          response.data.data
        ) {
          commit('SET_CIRCULAR_HIERARCHY_DATA', response.data.data)
        }
        return response.data.status === 200 && response.data.isSuccess
      } catch (error) {
        throw new Error(error)
      }
    },

    async relationshipTypeErrorValidaton(
      { commit },
      { masterSgaId, slaveSgaIds }
    ) {
      commit('SET_DUP_ENTITIES_DATA', null)
      try {
        const response = await Vue.prototype.$http.post(
          '/dmp/exception-handling/relationship-type-error-validation',
          { masterSgaId: masterSgaId, slaveSgaIds: slaveSgaIds }
        )

        if (
          response.data.status === 200 &&
          response.data?.isDup &&
          response.data.data
        ) {
          commit('SET_DUP_ENTITIES_DATA', response.data.data)
        }
        return response.data.status === 200 && response.data.isSuccess
      } catch (error) {
        throw new Error(error)
      }
    },

    async replaceHierarchyWithMaster(
      { commit },
      { masterSgaId, slaveSgaIds, userId }
    ) {
      try {
        const response = await Vue.prototype.$http.post(
          '/dmp/exception-handling/replace-with-master',
          { masterSgaId: masterSgaId, slaveSgaIds: slaveSgaIds, userId: userId }
        )
        return response.data.status === 200 && response.data.isSuccess
      } catch (error) {
        throw new Error(error)
      }
    },

    async ultimateParentValidation({ commit }, { masterSgaId, slaveSgaIds }) {
      try {
        const response = await Vue.prototype.$http.post(
          '/dmp/exception-handling/ultimate-parent-validation',
          { masterSgaId: masterSgaId, slaveSgaIds: slaveSgaIds }
        )
        if (response.data.status === 200 && response.data.data) {
          commit('SET_UP_ERROR_REF_DATA', response.data.data)
        }
        return response.data.status === 200 && response.data.isSuccess
      } catch (error) {
        throw new Error(error)
      }
    },

    async replaceHierarchyWithMasterAndProcessDuplicate(
      { commit },
      { masterSgaId, slaveSgaIds, duplicateBaseSgaId }
    ) {
      try {
        const response = await Vue.prototype.$http.post(
          '/dmp/exception-handling/replace-with-master-and-process-duplicate',
          { masterSgaId: masterSgaId, slaveSgaIds: slaveSgaIds, duplicateBaseSgaId: duplicateBaseSgaId }
        )
        return response.data.status === 200 && response.data.isSuccess
      } catch (error) {
        throw new Error(error)
      }
    },
  },

  mutations: {
    SET_EXCEPTION_HANDLING(state, exceptionHandling) {
      state.exceptionHandlingList = exceptionHandling
    },
    SET_SEARCH_QUERY(state, searchQuery) {
      state.searchQuery = searchQuery
    },
    SET_EXCEPTIONTYPE_FILTER(state, exceptionTypeFilter) {
      state.exceptionTypeFilter = exceptionTypeFilter
    },
    SET_EXCEPTIONAL_PAGINATION_DATA(state, paginationData) {
      state.limit = paginationData.limit
      state.offset = paginationData.offset
      state.totalRecords = paginationData.totalRows
    },
    SET_LIMIT_AND_OFFSET(state, { limit = 10, offset = 0 } = {}) {
      state.limit = limit
      state.offset = offset
    },
    SET_EXCEPTION_HANDLING_DUPLICATES(state, exceptionHandling) {
      const exceptionHandlingWithSelected = exceptionHandling?.map(
        (item, index) => ({
          ...item,
          selected: false,
          isMaster: item?.masterEntity === item.entityId,
          isDuplicate: item?.isEntityProcessed === 'true'
        })
      )

      state.exceptionHandlingDuplicates = exceptionHandlingWithSelected
    },

    SET_MULTIPLE_UP_DATA(state, multipleUPData) {
      state.multipleUPData = multipleUPData
    },

    SET_CIRCULAR_HIERARCHY_DATA(state, circularHierarchyData) {
      state.circularHierarchyData = circularHierarchyData
    },

    SET_MULTIPLE_IP_DATA(state, multipleIpData) {
      state.multipleIpData = multipleIpData
    },

    SET_DUP_ENTITIES_DATA(state, dupEntitiesData) {
      state.dupEntitiesData = dupEntitiesData
    },

    SET_UP_ERROR_REF_DATA(state, upErrorRefData) {
      state.upErrorRefData = upErrorRefData
    },

    SET_FETCH_FAILED_FLAG(state, failedToFetchFlag) {
      state.failedToFetchFlag = failedToFetchFlag
    }
  }
}
